import { Grid, Typography } from "@mui/material";
import { VerityAssessment } from "@verity/api-client/types";
import { ExemptedStudent } from "@verity/components";
import { CoreBanner, CoreButton, CoreChip, CoreFileCard, CoreSkeletonWrapper, CoreTabs } from "@verity/components/core";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../../../../helpers/api-client.js";
import i18n from "../../../../i18n.js";
import { usePortalStateContext } from "../../../../portal/context";
import { formatEventTimestamp } from "../../ProctoringResultsPage/ProctoringEventsTable.js";
import { LiveSessionNote, QuizInfoSummary, SessionInfo } from "../../TestResultsWrapper.js";
import styles from "./ProctoringResultView.module.css";
import ProctoringVideoPlayer from "./ProctoringVideoPlayer.js";
import { QuizResultPanel } from "./QuizResultPanel.js";
import EventsDownloadMenu from "./Timestamps/EventsDownloadMenu.js";
import { TimestampLinks } from "./Timestamps/TimestampLinks.js";
import { useAudioTranscriptFromDataDump } from "./Timestamps/timestamp-download-utils.js";
import { VideoWrapper } from "./VideoControls/VideoControlLayout.js";
import AttemptSelector from "./components/AttemptSelector.js";
import AttemptSelectorNew from "./components/AttemptSelectorNew.js";
import NeutralResultViewSkeleton from "./components/NeutralResultViewSkeleton.js";
import ResultViewBackButton from "./components/ResultViewBackButton.js";
import SessionStatusChip from "./components/StatusChip.js";
import StudentSelector from "./components/StudentSelector.js";
import StudentSelectorLayout from "./components/StudentSelectorLayout.js";
import SummaryButton from "./components/SummaryButton.js";
import SummaryPanel from "./components/SummaryPanel.js";
import useProctoringResultView, { UseProctoringResultViewReturn } from "./hooks/useProctoringResultView.js";
import useVerityAssessmentResult from "./hooks/useVerityAssessmentResult.js";
import { getAssignmentDraft, getAssignmentResponseId } from "./hooks/verityAssessmentResultHelper.js";
import DeleteConfirmationModal from "./modals/DeleteConfirmationModal.js";
import UnsavedChangesModal from "./modals/UnsavedChangesModal.js";
import { roomscanDisplayStateAtom } from "./store.js";
import AssignmentPanel from "./tabs/AssignmentPanel.js";
import ConfigurationPanel from "./tabs/ConfigurationPanel.js";
import IdPhotosPanel from "./tabs/IdPhotosPanel.js";
import LiveNotesPanel from "./tabs/LiveNotesPanel.js";
import NotesPanel, { RoomScanNotePanel } from "./tabs/NotePanel.js";
import ReferenceMaterialPanel from "./tabs/ReferenceMaterialPanel.js";
import RoomScanPanel from "./tabs/RoomScanPanel.js";
import ScratchNote from "./tabs/ScratchNote.js";
import SignatureTabPanel from "./tabs/SignaturePanel.js";

export type ProctorGroup = Awaited<ReturnType<typeof apiClient.liveClient.getLiveProctorGroup>>;
export type LiveSessionInfo = {
	id: string;
	name: string;
	isPopIn: boolean;
	proctorGroups: (Partial<ProctorGroup> & { id: number })[];
};

const VIDEO_JS_OPTIONS = {
	controls: true,
	responsive: true,
	aspectRatio: "16:9",
	fluid: true,
	userActions: {
		click: false,
	},
	controlBar: {
		playToggle: false,
		volumePanel: false,
		progressControl: false,
		remainingTimeDisplay: false,
		pictureInPictureToggle: false,
		fullscreenToggle: false,
	},
	bigPlayButton: false,
};

const LMS_EVENTS_PANEL_HEIGHT = "90vh";

// Integrity score itself now comes from the backend (getSessionIntegrityScore in
// backend/general-node/src/helper/reports.ts, via getLiveSessionIntegrityScoreSql) for parity with the
// batch reports' scoring. Only the score → badge color mapping stays client-side.
const getIntegrityBadgeColor = (score: number) => {
	if (score < 30) {
		return { border: "#FAC8C8", background: "#FFEFEF", text: "#9F0712" };
	} else if (score < 70) {
		return { border: "#FFD4C4", background: "#FFF1EB", text: "#CB3701" };
	} else {
		return { border: "#C2EFC9", background: "#E3F8E6", text: "#03692B" };
	}
};

export interface VideoPlaybackState {
	timer: number;
	lastTime: number;
	skip: boolean;
}

type PlayAction = {
	type: "play";
	speed: number;
};

type PauseAction = {
	type: "pause";
};

type SkipAction = {
	type: "skip";
	time: number;
};

export type VideoPlaybackAction = PlayAction | PauseAction | SkipAction;

function SelectorSkeleton({ width, labelWidth }: { width: string | number; labelWidth: number }) {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "flex-start",
				gap: "2px",
				width,
				maxWidth: "100%",
				fontSize: "1rem",
			}}
		>
			<CoreSkeletonWrapper loading width={labelWidth} height={14} sx={{ marginLeft: "0.3rem" }}>
				<div />
			</CoreSkeletonWrapper>
			<CoreSkeletonWrapper loading width="100%" height={36}>
				<div />
			</CoreSkeletonWrapper>
		</div>
	);
}

type ProctoringResultLayoutProps = {
	viewProps: Omit<ProctoringResultViewProps, "showTimestamps" | "readOnly"> & {
		showTimestamps: boolean;
		readOnly: boolean;
	};
	logic: UseProctoringResultViewReturn;
};

function LmsAssessmentLayout({ viewProps, logic }: ProctoringResultLayoutProps) {
	const { showTimestamps, readOnly, hideActionsMenu } = viewProps;
	const {
		componentsControl,
		summaryControl,
		proctoringState,
		session,
		eventsControl,
		isLoading,
		notesControl,
		webcamControl,
		roomScanControl,
		timeOffset,
		tabControl,
		fullScreenControl,
		screenShareControl,
		proctoredMediaContainer,
		referenceMaterialControl,
		retryVideoSources,
	} = logic;

	const { assessmentName, studentName } = proctoringState;
	const { userInfo, sessionNumber, totalSessions, selectedProctoringSession, sessionUuid, sessionStatus } = session;
	const { all: events, startDate } = eventsControl;
	const integrity = useMemo(
		() => ({
			score: eventsControl.integrityScore,
			color: getIntegrityBadgeColor(eventsControl.integrityScore),
		}),
		[eventsControl.integrityScore],
	);

	const { roomScan: roomScanNotesControl, session: sessionNotesControl } = notesControl;

	// One flag behind every skeleton in this layout so they all clear together, instead of popping in
	// piecemeal as each source resolves.
	const showSkeletons = useMemo(() => isLoading || !logic.areVideosFetched, [isLoading, logic.areVideosFetched]);

	// Mirrors the summary panel focusing its own close button on mount (see MainSummarySection in
	// Summary.tsx): closing it hands focus back to the button that opened it. Can't just focus in
	// onClose — the panel renders *in place of* the events table below, so the summary button doesn't
	// exist yet at that point. Flag the intent there and focus once it has remounted.
	const summaryButtonRef = useRef<HTMLButtonElement>(null);
	const restoreSummaryFocusRef = useRef(false);

	const handleSummaryClose = () => {
		restoreSummaryFocusRef.current = true;
		summaryControl.hide();
	};

	const [highlightedEventKey, setHighlightedEventKey] = useState<string | undefined>(undefined);

	useEffect(() => {
		// Gated on the flag, not on `showing` alone, so the panel closing for any other reason (a session
		// change resets it) doesn't pull focus out from under the user.
		if (summaryControl.showing || !restoreSummaryFocusRef.current) return;
		restoreSummaryFocusRef.current = false;
		summaryButtonRef.current?.focus();
	}, [summaryControl.showing]);

	// Shows the assessment name in the portal shell's top bar instead of next to this page's own back
	// button (see ResultViewBackButton below, which omits backLabel in this new layout).
	const { setPageTitle } = usePortalStateContext();
	useEffect(() => {
		setPageTitle(assessmentName);
		return () => setPageTitle(undefined);
	}, [assessmentName, setPageTitle]);

	const { inFullscreen } = fullScreenControl;
	const { mainTab, secondaryTab } = tabControl;
	const showRoomScanNotes = mainTab === "roomScan" && !readOnly;

	return (
		<Grid container sx={{ backgroundColor: "#F7F7F9", minHeight: "100%" }}>
			<Grid
				item
				container
				md={12}
				lg={7}
				flexDirection="column"
				paddingBottom="14px"
				height="100%"
				sx={{ minWidth: 0 }}
			>
				<Grid
					item
					container
					display="flex"
					alignItems="flex-end"
					justifyContent="flex-start"
					gap="12px"
					borderBottom="1px solid #F3F4F6"
					paddingX="2%"
					paddingY="1%"
					sx={{ backgroundColor: "#FFFFFF" }}
				>
					<ResultViewBackButton
						showBackButton={componentsControl.back.showBackButton}
						onBackButtonClick={componentsControl.back.handleBackButton}
					/>

					{/* Width mirrors --selector-container-width in StudentSelector.module.css. */}
					<CoreSkeletonWrapper
						loading={showSkeletons}
						customSkeleton={<SelectorSkeleton width="calc(15ch + 224px)" labelWidth={80} />}
					>
						<StudentSelector
							onBack={componentsControl.selectStudent.handleSelectPreviousStudent}
							onNext={componentsControl.selectStudent.handleSelectNextStudent}
							onStudentChange={componentsControl.selectStudent.handleSelectStudent}
							studentList={viewProps.studentList}
							selectedStudent={viewProps.selectedStudent}
							readOnly={readOnly}
							heading={readOnly ? viewProps.testTakerHeading : undefined}
						/>
					</CoreSkeletonWrapper>

					<CoreSkeletonWrapper
						loading={showSkeletons}
						customSkeleton={<SelectorSkeleton width={250} labelWidth={60} />}
					>
						<AttemptSelectorNew
							sessionNumber={sessionNumber}
							onChange={session.updateSessionIndex}
							proctoringSessions={viewProps.proctoringSessions}
						/>
					</CoreSkeletonWrapper>

					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							paddingBottom: "0.35rem",
						}}
					>
						<CoreSkeletonWrapper loading={showSkeletons} width={155} height={26} sx={{ borderRadius: "1rem" }}>
							<div
								style={{
									border: `1px solid ${integrity.color.border}`,
									borderRadius: "1rem",
									padding: "2px 10px",
									backgroundColor: integrity.color.background,
									color: integrity.color.text,
									fontWeight: 400,
									fontSize: "15px",
								}}
							>
								{`${i18n.t("result.components.resultsPage.proctoringResultsViewTSX.integrityBadge")} ${integrity.score}%`}
							</div>
						</CoreSkeletonWrapper>
					</div>
				</Grid>

				<Grid item container sx={{ padding: "12px", minWidth: 0 }}>
					<Grid
						item
						container
						flexDirection="column"
						rowGap="10px"
						sx={{
							padding: "12px",
							backgroundColor: "#FFFFFF",
							borderRadius: "0.6rem",
							minWidth: 0,
							overflow: "hidden",
						}}
					>
						<Grid item container sx={{ minWidth: 0 }}>
							<CoreSkeletonWrapper loading={showSkeletons} width={"100%"} height={"48px"}>
								<CoreTabs
									tabs={tabControl.mainTabList}
									value={mainTab}
									secondary
									height={"45px"}
									onChange={(_, newTab) => {
										tabControl.handleMainTabChange(newTab);
									}}
									tabsProps={{
										"aria-label": i18n.t(
											"result.components.resultsPage.proctoringResultsViewTSX.mainTabsAriaLabel",
										) as string,
										sx: {
											maxWidth: "100%",
										},
										ref: tabControl.mainTabsRef,
									}}
									tabPanelId={tabControl.mainTabPanelId}
									scrollable
									scrollButtons={tabControl.mainTabOverflow}
								/>
							</CoreSkeletonWrapper>
						</Grid>

						<Grid
							item
							sx={{ minHeight: "60vh", width: "100%", minWidth: 0 }}
							id={tabControl.mainTabPanelId}
							role="tabpanel"
							aria-labelledby={tabControl.mainTabIdRecord[mainTab]}
							key={mainTab}
							ref={componentsControl.mediaGrid.mediaGridRef}
						>
							{/* One placeholder for the whole panel rather than per tab: every tab's content is
							    driven by the same session fetch, and they all occupy this one slot. */}
							<CoreSkeletonWrapper loading={showSkeletons} width={"100%"} height={"60vh"}>
								<>
									{mainTab === "recording" && (
										<VideoWrapper inFullscreen={inFullscreen} showTimestamps={showTimestamps}>
											<ProctoringVideoPlayer
												isFullscreen={inFullscreen}
												sessionId={sessionUuid}
												showTimestamps={showTimestamps}
												controlOptions={webcamControl.options}
												playback={webcamControl.playback}
												playbackDispatch={webcamControl.dispatch}
												sessionRecordingTimestamps={eventsControl.sessionRecordingTimestamps}
												videos={{ webcam: webcamControl.sources, screencapture: webcamControl.screenSources }}
												startDate={startDate}
												duration={webcamControl.duration}
												timeOffset={timeOffset}
												setFullscreen={fullScreenControl.setFullscreen}
												setPlay={webcamControl.play}
												setVolume={(e) => webcamControl.setVolume(Number(e.target.value))}
												setSpeed={webcamControl.setSpeed}
												onEmailModalChange={componentsControl.modal.toggleEmailModal}
												shouldShowDownload={!readOnly && webcamControl.shouldShowDownload}
												showVideoUnavailable={webcamControl.showVideoUnavailable}
												showAudioThumbnail={webcamControl.showAudioThumbnail}
												webcamVideoOptions={VIDEO_JS_OPTIONS}
												screencaptureVideoOptions={VIDEO_JS_OPTIONS}
												shouldHideCamVid={webcamControl.shouldHideCamVid}
												events={events}
												sessionNotes={sessionNotesControl.notes}
												visibleLiveNotes={notesControl.visibleLiveNotes}
												toggleVolume={webcamControl.toggleVolume}
												isWebcamAndScreencaptureDeleted={selectedProctoringSession?.isWebcamAndScreenCaptureDeleted}
												webcamAndScreencaptureDeleteCause={selectedProctoringSession?.webcamAndScreenCaptureDeleteCause}
												setIsDeleteConfirmationModalShown={componentsControl.modal.toggleDeleteConfirmationModal}
												hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
												sessionStatus={sessionStatus}
												webCamHasError={webcamControl.hasError}
												setWebCamHasError={webcamControl.toggleError}
												selectedProctoringSessionUuid={sessionUuid}
												showMachineLearningOverlay={webcamControl.showMachineLearningOverlay}
												screenCaptureHasError={screenShareControl.hasError}
												setScreenCaptureHasError={screenShareControl.toggleError}
												proctoredMediaContainer={proctoredMediaContainer}
												sessionRecordingLimitHours={webcamControl.sessionRecordingLimitHours}
												onRetrySources={retryVideoSources}
											/>
										</VideoWrapper>
									)}
									{mainTab === "roomScan" && (
										<RoomScanPanel
											arrStamps={roomScanControl.timestampsArr}
											controls={roomScanControl.options}
											dispatch={roomScanControl.dispatch}
											displayStateAtom={roomscanDisplayStateAtom}
											isDeleted={selectedProctoringSession?.isRoomScanDeleted}
											deleteCause={selectedProctoringSession?.roomScanDeleteCause}
											isFullscreen={inFullscreen}
											onPlayVideo={roomScanControl.play}
											options={VIDEO_JS_OPTIONS}
											setVideoDuration={roomScanControl.changeDuration}
											videoTimer={roomScanControl.playback}
											sources={roomScanControl.sources}
											startDate={startDate}
											toggledTimestamps={showTimestamps}
											videoHasError={roomScanControl.hasError}
											setVideoHasError={roomScanControl.toggleError}
											notes={roomScanNotesControl.notes}
											hasFinishedPlaying={roomScanControl.hasFinishedPlaying}
											isOverlayShowing={roomScanControl.isOverlayShowing}
											hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
											sessionStatus={sessionStatus}
											maxDuration={roomScanControl.duration}
											volumeProps={{
												showVolume: !selectedProctoringSession?.isRoomScanDeleted,
												onVolumeButtonClick: roomScanControl.toggleVolume,
												onVolumeInputChange: (e) => roomScanControl.setVolume(Number(e.target.value)),
											}}
											onVideoSpeedChange={roomScanControl.setSpeed}
											onDeleteRecording={() => componentsControl.modal.toggleDeleteConfirmationModal("roomscan")}
											onFullScreenClick={fullScreenControl.setFullscreen}
										/>
									)}
									{mainTab === "idPhotos" && (
										<IdPhotosPanel
											sessionUuid={sessionUuid}
											handleImgError={componentsControl.image.handleImageError}
										/>
									)}
									{mainTab === "signature" && (
										<SignatureTabPanel
											imageURL={session.signatureUrl.value}
											signatureType={selectedProctoringSession?.signatureType}
										/>
									)}
									{mainTab === "scratchNote" && <ScratchNote images={session.scratchNoteLinks.value} />}
									{mainTab === "config" && (
										<ConfigurationPanel
											proctoringAssessmentConfiguration={proctoringState.proctoringOptions}
											proctoringData={proctoringState.proctoringConfigData}
											verityInstituteSettings={proctoringState.verityInstituteSettings}
											optionRelatedInstituteSettings={proctoringState.optionRelatedInstituteSettings}
											referenceMaterialFiles={
												referenceMaterialControl.mode === "instructor" ? referenceMaterialControl.files : undefined
											}
										/>
									)}
									{mainTab === "referenceMaterial" && (
										<ReferenceMaterialPanel
											loading={referenceMaterialControl.loading}
											files={referenceMaterialControl.files ?? []}
											mode={referenceMaterialControl.mode}
										/>
									)}
								</>
							</CoreSkeletonWrapper>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid
				item
				container
				md={12}
				lg={5}
				flexDirection={"column"}
				sx={{
					borderLeft: { lg: "0.5px solid #E5E7EB", md: "none" }, // Apply border-left at md and above
					borderTop: { xs: "0.5px solid #E5E7EB", md: "0.5px solid #E5E7EB", lg: "none" }, // Apply border-top at xs and below
					// Hugs its own content instead of stretching to the grid row's height, which the left
					// column sets whenever the video area makes it the taller of the two. Stretched, the
					// leftover below this column's fixed-height panel showed up as a stub of the border above
					// with nothing beside it.
					alignSelf: "flex-start",
				}}
			>
				{!showRoomScanNotes && (
					<>
						{tabControl.secondaryTabList.length > 1 && (
							<CoreTabs
								tabs={tabControl.secondaryTabList}
								width="100%"
								tabsProps={{
									"aria-label": i18n.t(
										"result.components.resultsPage.proctoringResultsViewTSX.secondaryTabsAriaLabel",
									) as string,
								}}
								tabProps={{
									sx: { width: `max(calc(100%/${tabControl.secondaryTabList.length}), 70px)` },
								}}
								tabPanelId={tabControl.secondaryTabPanelId}
								height={"48px"}
								value={secondaryTab}
								onChange={(_, newTab) => {
									tabControl.handleSecondaryTabChange(newTab);
									if (newTab === "notes") {
										void sessionNotesControl.refresh();
									}
								}}
							/>
						)}
						<div
							style={{ width: "100%" }}
							id={tabControl.secondaryTabList.length > 1 ? tabControl.secondaryTabPanelId : undefined}
							role={tabControl.secondaryTabList.length > 1 ? "tabpanel" : undefined}
							aria-labelledby={
								tabControl.secondaryTabList.length > 1 ? tabControl.secondaryTabIdRecord[secondaryTab] : undefined
							}
							key={secondaryTab}
						>
							{secondaryTab === "flaggedEvents" &&
								(summaryControl.showing ? (
									<SummaryPanel
										getDefaultSummary={summaryControl.getDefault}
										onClose={handleSummaryClose}
										onSave={summaryControl.save}
										isLiveSessionProctor={proctoringState.isLiveSessionProctor}
										sessionSummary={summaryControl.summary}
									/>
								) : (
									<TimestampLinks
										sessionId={sessionUuid}
										readOnly={readOnly}
										events={events}
										dispatch={webcamControl.dispatch}
										arrStamps={webcamControl.timestampsArr}
										sources={webcamControl.sources[0] ?? {}}
										offsetGetter={timeOffset}
										isLoading={showSkeletons}
										panelHeight={LMS_EVENTS_PANEL_HEIGHT}
										duration={webcamControl.duration ?? 0}
										updateEvents={eventsControl.update}
										summaryButton={
											!proctoringState.hasLms || readOnly ? undefined : (
												<SummaryButton
													ref={summaryButtonRef}
													isLiveSessionProctor={proctoringState.isLiveSessionProctor}
													showAiSummary={summaryControl.showAi}
													showSummary={summaryControl.show}
													loading={summaryControl.loading}
													sessionSummary={summaryControl.summary}
												/>
											)
										}
										downloadMenu={
											!proctoringState.hasLms || hideActionsMenu ? undefined : (
												<EventsDownloadMenu
													events={events}
													offsetGetter={timeOffset}
													duration={webcamControl.duration ?? 0}
													assessmentName={assessmentName}
													studentName={studentName}
													sessionId={sessionUuid}
													courseName={proctoringState.courseName}
													attemptNumber={sessionNumber}
													assessmentStatus={selectedProctoringSession?.status ?? "Unknown"}
													recordingDate={eventsControl.firstEventDate?.toLocaleDateString()}
													reportProps={{
														userInfo,
														sessionNum: sessionNumber.toString(),
														totalSessions,
														severeCount: eventsControl.severeCount ?? 0,
														majorCount: eventsControl.majorCount ?? 0,
														minorCount: eventsControl.minorCount ?? 0,
														sessionNotes: sessionNotesControl.notes,
														webcamVidSources: webcamControl.sources[0] ?? {},
														webcamTimestampsArr: webcamControl.timestampsArr,
														duration: webcamControl.duration,
														timeOffset,
														allEvents: events,
														roomScanVidSource: roomScanControl.sources,
														roomScanNotes: roomScanNotesControl.notes,
														roomScanDuration: roomScanControl.duration,
														roomScanTimestampsArr: roomScanControl.timestampsArr,
														personPhotoAvailable: Boolean(selectedProctoringSession?.personPhotoAvailable),
														idPhotoAvailable: Boolean(selectedProctoringSession?.idPhotoAvailable),
														uuid: sessionUuid,
														signatureType: selectedProctoringSession?.signatureType,
													}}
													createEventProps={
														readOnly
															? undefined
															: {
																	displayedTime: formatEventTimestamp(
																		Math.max(webcamControl.playback.timer - timeOffset, 0),
																		webcamControl.duration ?? 0,
																	),
																	pausePlayback: () => {
																		if (webcamControl.options.isPlaying) {
																			webcamControl.play();
																		}
																	},
																	onSave: async ({ message, flagLevel }) => {
																		const createdEvent = await apiClient.proctoringSession.createCustomEvent({
																			proctoringSessionId: sessionUuid,
																			message,
																			flagLevel,
																			time: new Date(startDate.getTime() + webcamControl.playback.timer),
																		});
																		setHighlightedEventKey(`event-${createdEvent.id}`);
																		await eventsControl.update();
																	},
																}
													}
													deleteAttemptProps={
														readOnly
															? undefined
															: {
																	attemptNumber: sessionNumber,
																	studentName,
																	onDelete: logic.deleteAttempt,
																}
													}
												/>
											)
										}
										severeCount={eventsControl.severeCount}
										majorCount={eventsControl.majorCount}
										minorCount={eventsControl.minorCount}
										highlightedEventKey={highlightedEventKey}
										setHighlightedEventKey={setHighlightedEventKey}
									/>
								))}
							{!readOnly && secondaryTab === "notes" && (
								<div role="tabpanel" className={styles.notesTabpanel}>
									<NotesPanel
										sessionNotes={sessionNotesControl.notes}
										dispatch={webcamControl.dispatch}
										duration={webcamControl.duration ?? 0}
										offsetGetter={timeOffset}
										onAddNote={sessionNotesControl.addNote}
										editNote={sessionNotesControl.editNote}
										deleteNote={sessionNotesControl.deleteNote}
										thumbnails={sessionNotesControl.thumbnail}
									/>
								</div>
							)}
							{secondaryTab === "liveNotes" && (
								<LiveNotesPanel
									liveNotes={notesControl.visibleLiveNotes}
									thumbnails={sessionNotesControl.thumbnail}
									dispatch={webcamControl.dispatch}
									duration={webcamControl.duration ?? 0}
									offsetGetter={timeOffset}
								/>
							)}
						</div>
					</>
				)}
				{showRoomScanNotes && (
					<RoomScanNotePanel
						dispatch={roomScanControl.dispatch}
						duration={roomScanControl.duration ?? 0}
						offsetGetter={0}
						onAddNote={roomScanNotesControl.addNote}
						editNote={roomScanNotesControl.editNote}
						deleteNote={roomScanNotesControl.deleteNote}
						sessionNotes={roomScanNotesControl.notes}
						thumbnails={roomScanNotesControl.thumbnail}
					/>
				)}
			</Grid>

			{componentsControl.modal.isDeleteConfirmationModalShown !== undefined && (
				<DeleteConfirmationModal
					onClose={() => componentsControl.modal.toggleDeleteConfirmationModal(undefined)}
					onDelete={componentsControl.modal.deleteRecording}
				/>
			)}
		</Grid>
	);
}

type VerityAssessmentLayoutProps = ProctoringResultLayoutProps & {
	verityAssessment: VerityAssessment;
};

function VerityAssessmentLayout({ logic, viewProps, verityAssessment }: VerityAssessmentLayoutProps) {
	const verityAssessmentLogic = useVerityAssessmentResult({ verityAssessment, session: logic.session });

	const {
		assignmentControl,
		totalPoints,
		earnedPoints,
		gradePercent,
		quizQuestions,
		quizResponses,
		savingGrade,
		handleSaveGrade,
		modal,
	} = verityAssessmentLogic;

	const { showTimestamps, readOnly, hideActionsMenu } = viewProps;
	const {
		componentsControl,
		summaryControl,
		proctoringState,
		session,
		eventsControl,
		isLoading,
		notesControl,
		webcamControl,
		roomScanControl,
		timeOffset,
		tabControl,
		fullScreenControl,
		screenShareControl,
		proctoredMediaContainer,
		referenceMaterialControl,
		retryVideoSources,
	} = logic;

	const { assessmentName, studentName } = proctoringState;
	const { sessionNumber, selectedProctoringSession, sessionUuid, sessionStatus } = session;
	const { all: events, startDate } = eventsControl;

	const { roomScan: roomScanNotesControl, session: sessionNotesControl } = notesControl;

	const { inFullscreen } = fullScreenControl;
	const { mainTab, secondaryTab } = tabControl;

	const verityAssignmentDraft = getAssignmentDraft(verityAssessmentLogic.grade.drafts);
	const hasUngradedResponses = quizResponses.some((r) => r.isUngraded);

	// One flag behind every skeleton in this layout so they all clear together, instead of popping in
	// piecemeal as each source resolves. The draft only gates assignments — quizzes never have one, so
	// folding it in unguarded would leave them skeletoned forever.
	const showSkeletons = useMemo(
		() =>
			isLoading ||
			verityAssessmentLogic.loading ||
			!logic.areVideosFetched ||
			(verityAssessment.type === "assignment" && !verityAssignmentDraft),
		[isLoading, verityAssessmentLogic.loading, logic.areVideosFetched, verityAssessment.type, verityAssignmentDraft],
	);

	const [highlightedEventKey, setHighlightedEventKey] = useState<string | undefined>(undefined);

	// Fetched here, rather than letting the kebab menu below fetch them itself, only so the same session's
	// transcripts aren't fetched twice when the caller supplies its own menu — both menus get these.
	const [transcripts] = useAudioTranscriptFromDataDump(sessionUuid);

	return (
		<Grid container sx={{ background: "#F7F7F9", minHeight: "100%" }}>
			<Grid item container md={12} lg={7} gap={"0px"} flexDirection={"column"} paddingBottom={"14px"} height={"100%"}>
				<div className={styles.verityAssessmentTopCard}>
					<div className={styles.verityAssessmentHeader}>
						<div className={styles.assessmentTitleWrapper}>
							{/* Not skeletoned as a whole: the close button has to stay live while the rest of the view
							    loads, so the drawer can be dismissed mid-load (the LMS layout above does the same).
							    Only the assessment name — unknown until the fetch lands — carries the skeleton. */}
							<ResultViewBackButton
								showBackButton={componentsControl.back.showBackButton}
								backLabel={assessmentName}
								labelLoading={showSkeletons}
								onBackButtonClick={() =>
									verityAssessmentLogic.handleAction(() => componentsControl.back.handleBackButton())
								}
							/>
							<CoreSkeletonWrapper loading={showSkeletons} width={180} height={"40px"}>
								<StudentSelectorLayout
									readOnly={readOnly}
									buttonType="icon"
									onBack={() =>
										verityAssessmentLogic.handleAction(() =>
											componentsControl.selectStudent.handleSelectPreviousStudent(),
										)
									}
									onNext={() =>
										verityAssessmentLogic.handleAction(() => componentsControl.selectStudent.handleSelectNextStudent())
									}
									onStudentChange={(studentId) =>
										verityAssessmentLogic.handleAction(() =>
											componentsControl.selectStudent.handleSelectStudent(studentId),
										)
									}
									studentList={viewProps.studentList}
									selectedStudent={viewProps.selectedStudent}
									removeBorderStyling={false}
									studentSelectorClassName={styles.studentSelectorVerity}
									className={styles.studentSelectorLayoutVerity}
								/>
							</CoreSkeletonWrapper>
						</div>
						<CoreSkeletonWrapper loading={showSkeletons} width={200} height={"48px"}>
							<div className={styles.saveButtonsWrapper}>
								<CoreButton
									color="secondary"
									onClick={() => handleSaveGrade(false)}
									disabled={verityAssessmentLogic.saveDisabled}
									loading={savingGrade === "draft"}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.saveLater")}
								</CoreButton>
								<CoreButton
									onClick={() => handleSaveGrade(true)}
									disabled={verityAssessmentLogic.saveDisabled}
									loading={savingGrade === "publish"}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.saveGrade")}
								</CoreButton>
							</div>
						</CoreSkeletonWrapper>
					</div>
					<div className={styles.assessmentInfoContainer}>
						<div className={styles.attemptInfoWrapper}>
							<CoreSkeletonWrapper loading={showSkeletons} width={140} height={"36px"}>
								<AttemptSelector
									sessionNumber={sessionNumber}
									onChange={(index) => verityAssessmentLogic.handleAction(() => session.updateSessionIndex(index))}
									proctoringSessions={viewProps.proctoringSessions}
									className={styles.verityAtttmptSelector}
								/>
							</CoreSkeletonWrapper>
							<CoreSkeletonWrapper loading={showSkeletons} width={90} height={"28px"}>
								<SessionStatusChip status={sessionStatus} isLate={verityAssessmentLogic.isLateSubmission} />
							</CoreSkeletonWrapper>
							<CoreSkeletonWrapper loading={showSkeletons} width={100} height={"28px"}>
								{verityAssessment.type === "quiz" && quizQuestions.length > 0 ? (
									<>
										<CoreChip chipColor="#F5E5DE" variant="rectangular" sx={{ minWidth: "unset" }}>
											{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questionsLabel", {
												defaultValue: "Questions:",
											})}{" "}
											{quizQuestions.length}
										</CoreChip>
										{!hasUngradedResponses && (
											<>
												<CoreChip chipColor="#E4E3F6" variant="rectangular" sx={{ minWidth: "unset" }}>
													{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.scoreLabel", {
														defaultValue: "Score:",
													})}{" "}
													{earnedPoints} / {totalPoints}
												</CoreChip>
												<CoreChip chipColor="#F9E4F5" variant="rectangular" sx={{ minWidth: "unset" }}>
													{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.gradeLabel", {
														defaultValue: "Grade:",
													})}{" "}
													{gradePercent}%
												</CoreChip>
											</>
										)}
									</>
								) : (
									<>
										{assignmentControl.display.showFileCountChip && (
											<CoreChip chipColor="#E4E3F6" variant="rectangular">
												{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.submittedFiles", {
													count: assignmentControl.files.student.length,
												})}
											</CoreChip>
										)}
										<CoreChip chipColor="#F9E4F5" variant="rectangular">
											{verityAssessmentLogic.numAttemptsText}
										</CoreChip>
									</>
								)}
							</CoreSkeletonWrapper>
						</div>
						{assignmentControl.display.showInstructorFiles && (
							<div className={styles.verityAssignmentInstructionsContainer}>
								<Typography variant="h2" className={styles.title}>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.assignmentInstructions")}
								</Typography>
								{assignmentControl.files.instructor.map((file, index) => (
									<CoreFileCard
										key={index}
										file={file}
										showDownload
										onRefetch={() => assignmentControl.fetch.instructor(file.name)}
									/>
								))}
							</div>
						)}
					</div>
				</div>
				{/* Whether there is a banner at all isn't known until the fetch lands (bannerType stays undefined
				    for a session with nothing to report), so the placeholder shows for every session and gives
				    way to nothing on the ones without one. The container carries its own padding, hence keeping
				    it inside the condition rather than around it — an empty one leaves a 20px gap. */}
				{(showSkeletons || verityAssessmentLogic.banner.bannerType !== undefined) && (
					<div className={styles.bannerContainer}>
						<CoreSkeletonWrapper loading={showSkeletons} width={"100%"} height={50} sx={{ borderRadius: "7px" }}>
							<CoreBanner label={verityAssessmentLogic.banner.text} color={verityAssessmentLogic.banner.bannerType} />
						</CoreSkeletonWrapper>
					</div>
				)}
				{verityAssessment.type === "quiz" && (
					<div className={styles.verityQuizContainer}>
						{/* Its own two skeletons (nav + content), but on the page's flag rather than its own, so
						    they clear with everything around them. */}
						<QuizResultPanel
							quizQuestions={quizQuestions}
							quizResponses={quizResponses}
							grade={verityAssessmentLogic.grade}
							loading={showSkeletons}
						/>
					</div>
				)}
				{verityAssessment.type === "assignment" && (
					<CoreSkeletonWrapper loading={showSkeletons} height={400}>
						<AssignmentPanel
							{...assignmentControl}
							grade={verityAssessmentLogic.grade}
							quizQuestions={verityAssessmentLogic.quizQuestions}
							draft={verityAssignmentDraft!}
							responseId={getAssignmentResponseId(verityAssessmentLogic.grade.drafts)}
						/>
					</CoreSkeletonWrapper>
				)}
			</Grid>
			<Grid
				item
				md={12}
				lg={5}
				sx={{
					background: "white",
					borderLeft: { lg: "0.5px solid #CACACA", md: "none" }, // Apply border-left at md and above
					borderTop: { xs: "0.5px solid #CACACA", md: "0.5px solid #CACACA", lg: "none" }, // Apply border-top at xs and below
				}}
				container
				flexDirection={"column"}
			>
				<div className={styles.verityTabsWrapper}>
					<CoreSkeletonWrapper loading={showSkeletons} width={"100%"} height={"48px"}>
						<CoreTabs
							tabs={tabControl.mainTabList}
							value={mainTab}
							secondary
							height={"36px"}
							onChange={(_, newTab) => {
								tabControl.handleMainTabChange(newTab);
							}}
							tabsProps={{
								"aria-label": i18n.t(
									"result.components.resultsPage.proctoringResultsViewTSX.mainTabsAriaLabel",
								) as string,
								sx: {
									maxWidth: "100%",
								},
								ref: tabControl.mainTabsRef,
							}}
							tabPanelId={tabControl.mainTabPanelId}
							scrollable
							scrollButtons={tabControl.mainTabOverflow}
						/>
					</CoreSkeletonWrapper>
				</div>
				<div className={styles.mainTabPanelWrapper}>
					<Grid
						item
						sx={{ height: "auto", maxHeight: "45vh", width: "100%" }}
						id={tabControl.mainTabPanelId}
						role="tabpanel"
						aria-labelledby={tabControl.mainTabIdRecord[mainTab]}
						key={mainTab}
						ref={componentsControl.mediaGrid.mediaGridRef}
					>
						{/* One placeholder for the whole panel rather than per tab: every tab's content is
						    driven by the same session fetch, and they all occupy this one slot. */}
						<CoreSkeletonWrapper loading={showSkeletons} width={"100%"} height={"45vh"}>
							<>
								{mainTab === "recording" && (
									<VideoWrapper inFullscreen={inFullscreen} showTimestamps={showTimestamps}>
										<ProctoringVideoPlayer
											isFullscreen={inFullscreen}
											sessionId={sessionUuid}
											showTimestamps={showTimestamps}
											controlOptions={webcamControl.options}
											playback={webcamControl.playback}
											playbackDispatch={webcamControl.dispatch}
											sessionRecordingTimestamps={eventsControl.sessionRecordingTimestamps}
											videos={{ webcam: webcamControl.sources, screencapture: webcamControl.screenSources }}
											startDate={startDate}
											duration={webcamControl.duration}
											timeOffset={timeOffset}
											setFullscreen={fullScreenControl.setFullscreen}
											setPlay={webcamControl.play}
											setVolume={(e) => webcamControl.setVolume(Number(e.target.value))}
											setSpeed={webcamControl.setSpeed}
											onEmailModalChange={componentsControl.modal.toggleEmailModal}
											shouldShowDownload={!readOnly && webcamControl.shouldShowDownload}
											showVideoUnavailable={webcamControl.showVideoUnavailable}
											showAudioThumbnail={webcamControl.showAudioThumbnail}
											webcamVideoOptions={VIDEO_JS_OPTIONS}
											screencaptureVideoOptions={VIDEO_JS_OPTIONS}
											shouldHideCamVid={webcamControl.shouldHideCamVid}
											events={events}
											sessionNotes={sessionNotesControl.notes}
											visibleLiveNotes={notesControl.visibleLiveNotes}
											toggleVolume={webcamControl.toggleVolume}
											isWebcamAndScreencaptureDeleted={selectedProctoringSession?.isWebcamAndScreenCaptureDeleted}
											webcamAndScreencaptureDeleteCause={selectedProctoringSession?.webcamAndScreenCaptureDeleteCause}
											setIsDeleteConfirmationModalShown={componentsControl.modal.toggleDeleteConfirmationModal}
											hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
											sessionStatus={sessionStatus}
											webCamHasError={webcamControl.hasError}
											setWebCamHasError={webcamControl.toggleError}
											selectedProctoringSessionUuid={sessionUuid}
											showMachineLearningOverlay={webcamControl.showMachineLearningOverlay}
											screenCaptureHasError={screenShareControl.hasError}
											setScreenCaptureHasError={screenShareControl.toggleError}
											proctoredMediaContainer={proctoredMediaContainer}
											sessionRecordingLimitHours={webcamControl.sessionRecordingLimitHours}
											onRetrySources={retryVideoSources}
										/>
									</VideoWrapper>
								)}
								{mainTab === "roomScan" && (
									<RoomScanPanel
										arrStamps={roomScanControl.timestampsArr}
										controls={roomScanControl.options}
										dispatch={roomScanControl.dispatch}
										displayStateAtom={roomscanDisplayStateAtom}
										isDeleted={selectedProctoringSession?.isRoomScanDeleted}
										deleteCause={selectedProctoringSession?.roomScanDeleteCause}
										isFullscreen={inFullscreen}
										onPlayVideo={roomScanControl.play}
										options={VIDEO_JS_OPTIONS}
										setVideoDuration={roomScanControl.changeDuration}
										videoTimer={roomScanControl.playback}
										sources={roomScanControl.sources}
										startDate={startDate}
										toggledTimestamps={showTimestamps}
										videoHasError={roomScanControl.hasError}
										setVideoHasError={roomScanControl.toggleError}
										notes={roomScanNotesControl.notes}
										hasFinishedPlaying={roomScanControl.hasFinishedPlaying}
										isOverlayShowing={roomScanControl.isOverlayShowing}
										hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
										sessionStatus={sessionStatus}
										maxDuration={roomScanControl.duration}
										volumeProps={{
											showVolume: !selectedProctoringSession?.isRoomScanDeleted,
											onVolumeButtonClick: roomScanControl.toggleVolume,
											onVolumeInputChange: (e) => roomScanControl.setVolume(Number(e.target.value)),
										}}
										onVideoSpeedChange={roomScanControl.setSpeed}
										onDeleteRecording={() => componentsControl.modal.toggleDeleteConfirmationModal("roomscan")}
										onFullScreenClick={fullScreenControl.setFullscreen}
									/>
								)}
								{mainTab === "idPhotos" && (
									<IdPhotosPanel sessionUuid={sessionUuid} handleImgError={componentsControl.image.handleImageError} />
								)}
								{mainTab === "signature" && (
									<SignatureTabPanel
										imageURL={session.signatureUrl.value}
										signatureType={selectedProctoringSession?.signatureType}
									/>
								)}
								{mainTab === "scratchNote" && <ScratchNote images={session.scratchNoteLinks.value} />}
								{mainTab === "config" && (
									<ConfigurationPanel
										proctoringAssessmentConfiguration={proctoringState.proctoringOptions}
										proctoringData={proctoringState.proctoringConfigData}
										verityInstituteSettings={proctoringState.verityInstituteSettings}
										optionRelatedInstituteSettings={proctoringState.optionRelatedInstituteSettings}
										referenceMaterialFiles={
											referenceMaterialControl.mode === "instructor" ? referenceMaterialControl.files : undefined
										}
									/>
								)}
								{mainTab === "referenceMaterial" && (
									<ReferenceMaterialPanel
										loading={referenceMaterialControl.loading}
										files={referenceMaterialControl.files ?? []}
										mode={referenceMaterialControl.mode}
									/>
								)}
							</>
						</CoreSkeletonWrapper>
					</Grid>
				</div>
				{mainTab !== "roomScan" && (
					<>
						{tabControl.secondaryTabList.length > 1 && (
							<CoreTabs
								tabs={tabControl.secondaryTabList}
								width="100%"
								tabsProps={{
									"aria-label": i18n.t(
										"result.components.resultsPage.proctoringResultsViewTSX.secondaryTabsAriaLabel",
									) as string,
								}}
								tabProps={{
									sx: { width: `max(calc(100%/${tabControl.secondaryTabList.length}), 70px)` },
								}}
								height={"48px"}
								value={secondaryTab}
								onChange={(_, newTab) => {
									tabControl.handleSecondaryTabChange(newTab);
									if (newTab === "notes") {
										void sessionNotesControl.refresh();
									}
								}}
								tabPanelId={tabControl.secondaryTabPanelId}
							/>
						)}
						<div
							style={{ width: "100%" }}
							id={tabControl.secondaryTabList.length > 1 ? tabControl.secondaryTabPanelId : undefined}
							role={tabControl.secondaryTabList.length > 1 ? "tabpanel" : undefined}
							aria-labelledby={
								tabControl.secondaryTabList.length > 1 ? tabControl.secondaryTabIdRecord[secondaryTab] : undefined
							}
							key={secondaryTab}
						>
							{secondaryTab === "flaggedEvents" &&
								(summaryControl.showing ? (
									<SummaryPanel
										getDefaultSummary={summaryControl.getDefault}
										onClose={summaryControl.hide}
										onSave={summaryControl.save}
										isLiveSessionProctor={proctoringState.isLiveSessionProctor}
										sessionSummary={summaryControl.summary}
									/>
								) : (
									<TimestampLinks
										sessionId={sessionUuid}
										readOnly={readOnly}
										events={events}
										dispatch={webcamControl.dispatch}
										arrStamps={webcamControl.timestampsArr}
										sources={webcamControl.sources[0] ?? {}}
										offsetGetter={timeOffset}
										isLoading={showSkeletons}
										panelHeight={componentsControl.mediaGrid.mediaGridHeight}
										duration={webcamControl.duration ?? 0}
										updateEvents={eventsControl.update}
										summaryButton={
											!proctoringState.hasLms || readOnly ? undefined : (
												<SummaryButton
													isLiveSessionProctor={proctoringState.isLiveSessionProctor}
													showAiSummary={summaryControl.showAi}
													showSummary={summaryControl.show}
													loading={summaryControl.loading}
													sessionSummary={summaryControl.summary}
												/>
											)
										}
										downloadMenu={
											!proctoringState.hasLms || hideActionsMenu ? undefined : (
												<EventsDownloadMenu
													events={events}
													offsetGetter={timeOffset}
													duration={webcamControl.duration ?? 0}
													assessmentName={assessmentName}
													studentName={studentName}
													sessionId={sessionUuid}
													courseName={proctoringState.courseName}
													attemptNumber={sessionNumber}
													assessmentStatus={selectedProctoringSession?.status ?? "Unknown"}
													recordingDate={eventsControl.firstEventDate?.toLocaleDateString()}
													createEventProps={
														readOnly
															? undefined
															: {
																	displayedTime: formatEventTimestamp(
																		Math.max(webcamControl.playback.timer - timeOffset, 0),
																		webcamControl.duration ?? 0,
																	),
																	pausePlayback: () => {
																		if (webcamControl.options.isPlaying) {
																			webcamControl.play();
																		}
																	},
																	onSave: async ({ message, flagLevel }) => {
																		const createdEvent = await apiClient.proctoringSession.createCustomEvent({
																			proctoringSessionId: sessionUuid,
																			message,
																			flagLevel,
																			time: new Date(startDate.getTime() + webcamControl.playback.timer),
																		});
																		setHighlightedEventKey(`event-${createdEvent.id}`);
																		await eventsControl.update();
																	},
																}
													}
													deleteAttemptProps={
														readOnly
															? undefined
															: {
																	attemptNumber: sessionNumber,
																	studentName,
																	onDelete: logic.deleteAttempt,
																}
													}
													transcripts={transcripts}
													boxed
												/>
											)
										}
										severeCount={eventsControl.severeCount}
										majorCount={eventsControl.majorCount}
										minorCount={eventsControl.minorCount}
										highlightedEventKey={highlightedEventKey}
										setHighlightedEventKey={setHighlightedEventKey}
									/>
								))}
							{!readOnly && secondaryTab === "notes" && (
								<div role="tabpanel" className={styles.notesTabpanel}>
									<NotesPanel
										sessionNotes={sessionNotesControl.notes}
										dispatch={webcamControl.dispatch}
										duration={webcamControl.duration ?? 0}
										offsetGetter={timeOffset}
										onAddNote={sessionNotesControl.addNote}
										editNote={sessionNotesControl.editNote}
										deleteNote={sessionNotesControl.deleteNote}
										thumbnails={sessionNotesControl.thumbnail}
									/>
								</div>
							)}
							{secondaryTab === "liveNotes" && (
								<LiveNotesPanel
									liveNotes={notesControl.visibleLiveNotes}
									thumbnails={sessionNotesControl.thumbnail}
									dispatch={webcamControl.dispatch}
									duration={webcamControl.duration ?? 0}
									offsetGetter={timeOffset}
								/>
							)}
						</div>
					</>
				)}
				{mainTab === "roomScan" && !readOnly && (
					<RoomScanNotePanel
						dispatch={roomScanControl.dispatch}
						duration={roomScanControl.duration ?? 0}
						offsetGetter={0}
						onAddNote={roomScanNotesControl.addNote}
						editNote={roomScanNotesControl.editNote}
						deleteNote={roomScanNotesControl.deleteNote}
						sessionNotes={roomScanNotesControl.notes}
						thumbnails={roomScanNotesControl.thumbnail}
					/>
				)}
			</Grid>
			{componentsControl.modal.isDeleteConfirmationModalShown !== undefined && (
				<DeleteConfirmationModal
					onClose={() => componentsControl.modal.toggleDeleteConfirmationModal(undefined)}
					onDelete={componentsControl.modal.deleteRecording}
				/>
			)}
			{modal.open && (
				<UnsavedChangesModal
					onCancel={modal.discard}
					onSaveLater={() => modal.save(false)}
					onSaveGrade={() => modal.save(true)}
					onClose={modal.close}
					attemptNumber={sessionNumber}
				/>
			)}
		</Grid>
	);
}

export type ProctoringResultViewProps = {
	// sessionUuid: string;
	instituteId: number;
	courseId: number;
	// sessionNum: string | undefined | null;
	showVideo: boolean;
	/**
	 * defaults to false
	 */
	showTimestamps?: boolean;
	selectedLiveNotes?: LiveSessionNote[];
	// selectedProctoringSession?: SessionInfo | null;
	// totalSessions?: number | null;
	proctoringSessions: SessionInfo[];
	liveSessions: Record<string, LiveSessionInfo>;
	setProctoringSessions?: Dispatch<SetStateAction<SessionInfo[] | undefined>>;
	setShowStudentResults?: (value: boolean) => void;
	assessmentInfo: QuizInfoSummary | null;
	selectedStudent?: number;
	setSelectedStudent: (value: number | undefined) => void;
	studentList: { id: number; name: string; severityLevel?: number }[];
	/**
	 * Relabels the test-taker block as a fixed heading — the public demo shows "Session Report" over
	 * "Prepared for <visitor's name>" where the portal shows "Test Taker" over a name.
	 *
	 * Read-only surfaces only; ignored otherwise, since a live selector's value is whatever the user
	 * picked. Passed down rather than done in CSS on purpose: hiding the stock label visually leaves it
	 * in the accessibility tree, and no CSS property can set an accessible name.
	 */
	testTakerHeading?: { label: string; value: string };
	initialProctoringSessionId?: string;
	exemptedStudents?: Omit<ExemptedStudent, "name">[];
	/**
	 * Called once the selected attempt has been deleted on the server, with its session uuid. The host owns
	 * what happens next, because it owns the data this view only reads: it has to drop the attempt from
	 * `proctoringSessions` and then decide where the view lands — the test-taker's next attempt, the next
	 * test-taker, or closing this view when nothing is left.
	 *
	 * Without it the "Delete Attempt" item still appears (any writable view may delete an attempt) and the
	 * view falls back to closing itself, since the attempt it was showing no longer exists.
	 */
	onAttemptDeleted?: (deletedSessionUuid: string) => void;
	/**
	 * Display only. Withholds every control that writes: add-custom-event, flag/resolve,
	 * collaboration-detected resolve, both notes panels and their tab, the edit-custom-event pencil and
	 * the test-taker selector. The **Attempt** selector stays — one student's own attempts are a read.
	 * Defaults to `false`, so the portal and the interview hub keep today's behaviour.
	 *
	 * The Summary button is also withheld: generating a summary is a `POST`, and the Lumina surface's
	 * fetch patch rewrites `GET` only (`lumina/results-api.ts`), so the call falls through to the portal
	 * path and fails unauthenticated — leaving the panel spinning on "Analyzing proctoring data..."
	 * for ever. The Actions menu is **not** gated on this: it is an instructor feature any Verity
	 * institution may use, so it keys on `proctoringState.hasLms` and is absent only for a standalone
	 * institution.
	 *
	 * Additive on purpose. `hasInstituteEditPermission` is **not** reusable for this — it defaults false,
	 * so gating on it would take note-taking and flag/resolve away from portal instructors who legitimately
	 * lack institute-edit (`SPEC-3.md` §10.3). Hiding the controls in CSS is not equivalent either: they
	 * stay keyboard-reachable and their handlers still fire.
	 */
	readOnly?: boolean;
	/** Drops the Configurations main tab. The public demo withholds it; `readOnly` does not, so Lumina keeps it. */
	hideConfigurationsTab?: boolean;
	/** Drops the Actions menu beside the flagged events, regardless of `proctoringState.hasLms`. */
	hideActionsMenu?: boolean;
};

export const ProctoringResultView = (props: ProctoringResultViewProps) => {
	const viewProps = { ...props, showTimestamps: props.showTimestamps ?? false, readOnly: props.readOnly ?? false };
	const logic = useProctoringResultView(viewProps);

	// Until we know whether this is a Verity assessment, don't commit to either layout's grid
	// structure (they differ enough that "wrong skeleton" would flash just as badly as "wrong layout"
	// so just render this generic box).
	if (logic.proctoringState.isAssessmentTypeLoading) {
		return <NeutralResultViewSkeleton />;
	}

	if (logic.proctoringState.verityAssessment) {
		return (
			<VerityAssessmentLayout
				viewProps={viewProps}
				logic={logic}
				verityAssessment={logic.proctoringState.verityAssessment}
			/>
		);
	}

	return <LmsAssessmentLayout viewProps={viewProps} logic={logic} />;
};

export default ProctoringResultView;
